import BatchedRecordLoader from 'components/framework/BatchedRecordLoader';
import { RecordLinkTypeEnum } from 'components/framework/Enums';
import {
  CardView,
  Epic,
  Feature,
  Goal,
  Idea,
  IdeaTheme,
  Initiative,
  KeyResult,
  Page,
  PdlcStep,
  Project,
  RecordLink,
  Release,
  ReleasePhase,
  Requirement,
  Task,
} from 'components/framework/models';
import {
  type RecordId,
  type RecordLookupOptions,
  type RecordLookupType,
  type ResolveRecordInfo,
  type WhiteboardRecordInfo,
} from 'javascripts/shared-types';
import Debug from 'debug';

const debug = Debug('whiteboard');

const RECORD_LINK_MODELS = {
  Epic,
  Feature,
  Goal,
  Idea,
  Initiative,
  Release,
  ReleasePhase,
  Requirement,
  KeyResult,
  PdlcStep,
};

const ALL_MODELS = {
  ...RECORD_LINK_MODELS,
  Page,
  Task,
  IdeaTheme,
  Project,
};

// Some record shapes in Whiteboards still use the collection name, so we need to map both
const MODELS = {
  ...ALL_MODELS,
  epics: Epic,
  features: Feature,
  goals: Goal,
  ideas: Idea,
  ideaThemes: IdeaTheme,
  initiatives: Initiative,
  pages: Page,
  releases: Release,
  releasePhases: ReleasePhase,
  requirements: Requirement,
  tasks: Task,
  keyResults: KeyResult,
  pdlcSteps: PdlcStep,
  projects: Project,
};

type ModelType = (typeof MODELS)[RecordLookupType];

function queryForModel(
  modelClass: ModelType,
  options: RecordLookupOptions = {}
) {
  let scope = modelClass.select('id', 'name');

  if (modelClass.hasField('workflowStatus')) {
    scope = scope.merge({ workflowStatus: ['color', 'name'] });
  }

  if (modelClass.hasField('assignedToUser')) {
    scope = scope.merge({
      assignedToUser: ['id', 'name', 'initials', 'avatarUrl'],
    });
  }

  for (const attr of [
    'score',
    'ideasCount',
    'votes',
    'referenceNum',
    'color',
  ]) {
    if (modelClass.hasField(attr)) {
      scope = scope.select(attr);
    }
  }

  if (modelClass === Task) {
    const taskModels = [Epic, Feature, Idea, Release, Requirement];
    const queries = taskModels.map(model => model.select('name'));
    scope = scope
      .where({ personal: false })
      .select('status', 'completedByMe', 'dueDate', 'completedDate')
      .merge({ record: queries.reduce((scope, query) => scope.union(query)) });
  } else if (modelClass === PdlcStep && window.pdlcFramework?.id) {
    scope = scope.where({ pdlcFrameworkId: window.pdlcFramework.id });
  } else if (modelClass === Page) {
    scope = scope.select(
      'referenceNum',
      'documentDate',
      'documentType',
      'emojiValue'
    );
  } else if (modelClass === Project) {
    scope = scope.select('referencePrefix');
  }

  if (options?.fetchRecordLinks && modelClass.hasField('parentRecordLinks')) {
    scope = scope.merge({
      parentRecordLinks: RecordLink.where({
        linkType: [
          RecordLinkTypeEnum.DEPENDS_ON,
          RecordLinkTypeEnum.IMPACTS,
          RecordLinkTypeEnum.BLOCKS,
        ],
        recordType: Object.keys(RECORD_LINK_MODELS),
      }).select(
        'childRecordId',
        'childRecordType',
        'relationDescription',
        'linkType',
        'inverted'
      ),
    });
  }

  const { cardView } = options;
  if (cardView instanceof CardView) {
    scope = cardView.buildScope(scope);
  }

  return scope;
}

let batchedRecordLoader: BatchedRecordLoader | undefined;

export const lookupRecordInfo: ResolveRecordInfo = async (
  recordType: RecordLookupType,
  recordId: RecordId,
  options?: RecordLookupOptions
) => {
  debug('lookupRecordInfo', recordType, recordId, options);

  // Record types must match https://github.com/aha-app/whiteboard/blob/main/src/tools/ToolPaste.js
  // Older versions of the code used inconsistent types, so both versions are supported here.
  const modelClass = MODELS[recordType];

  if (modelClass) {
    const query = queryForModel(modelClass, options);

    if (!batchedRecordLoader) {
      batchedRecordLoader = new BatchedRecordLoader();
      setTimeout(() => {
        if (batchedRecordLoader) {
          batchedRecordLoader.run();
        }
      }, 0);
      setTimeout(() => {
        batchedRecordLoader = undefined;
      }, 1000);
    }

    return batchedRecordLoader.loadRecord(
      query,
      recordId
    ) as unknown as WhiteboardRecordInfo;
  } else {
    debug('Cannot look up info for unknown record type', recordType);
    return { typename: 'Undefined' };
  }
};
